import { useNavigate, useSearchParams } from 'react-router-dom';
import { Spinner } from '@klaro7/ui';

import { useDailyRevenue, useOnlineUsers } from '@/features/revenue/api';
import { TariffBadges } from '@/features/calls/lib/tariffProvenance';

function todayLocal(): string {
  return new Date().toLocaleDateString('en-CA');
}

function shiftDay(date: string, days: number): string {
  const d = new Date(`${date}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

function formatTime(iso: string): string {
  return new Date(iso).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' });
}

function formatEuro(n: number | null): string {
  return n == null ? '—' : `€ ${n.toFixed(2)}`;
}

function formatDuration(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

const callTypeLabels: Record<string, string> = { audio: 'Audio', video: 'Video', chat: 'Chat' };
const statusLabels: Record<string, string> = {
  completed: 'Conclusa',
  active: 'In corso',
  paused: 'In pausa',
  failed: 'Fallita',
  ringing: 'Squillo',
  missed: 'Persa',
  rejected: 'Rifiutata',
  cancelled: 'Annullata',
};

function AnsweredBadge({ answered }: { answered: boolean }) {
  return answered ? (
    <span
      aria-label="Con risposta"
      className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-emerald-100 text-sm font-bold text-emerald-600"
    >
      ✓
    </span>
  ) : (
    <span
      aria-label="Senza risposta"
      className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-red-100 text-sm font-bold text-red-500"
    >
      ✕
    </span>
  );
}

/** % vs the previous day; falls back to the raw count when yesterday was 0. */
function DeltaBadge({ current, previous, compareLabel }: { current: number; previous: number; compareLabel: string }) {
  if (previous === 0) {
    return <span className="text-xs font-medium text-neutral-400">{compareLabel}: 0</span>;
  }
  const pct = Math.round(((current - previous) / previous) * 100);
  const tone = pct > 0 ? 'bg-emerald-100 text-emerald-700' : pct < 0 ? 'bg-red-100 text-red-600' : 'bg-neutral-100 text-neutral-500';
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold ${tone}`}>
      {pct > 0 ? '+' : ''}
      {pct}% <span className="font-normal opacity-70">vs {compareLabel}</span>
    </span>
  );
}

function StatCard({
  title,
  value,
  detail,
  delta,
}: {
  title: string;
  value: string;
  detail: string;
  delta?: { current: number; previous: number; compareLabel: string };
}) {
  return (
    <div className="rounded-xl border border-neutral-200 bg-white p-5">
      <p className="text-sm font-medium text-neutral-500">{title}</p>
      <div className="mt-2 flex items-baseline gap-2">
        <p className="text-3xl font-bold text-neutral-900">{value}</p>
        {delta && <DeltaBadge {...delta} />}
      </div>
      <p className="mt-1 text-sm text-neutral-500">{detail}</p>
    </div>
  );
}

/** Live connected-users card; independent from the selected day (it's "now"). */
function OnlineUsersCard() {
  const { data } = useOnlineUsers();
  return (
    <div className="rounded-xl border border-neutral-200 bg-white p-5">
      <p className="flex items-center gap-2 text-sm font-medium text-neutral-500">
        <span className="relative flex h-2.5 w-2.5">
          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
          <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-emerald-500" />
        </span>
        Utenti online ora
      </p>
      <p className="mt-2 text-3xl font-bold text-neutral-900">{data ? data.total : '—'}</p>
      <p className="mt-1 text-sm text-neutral-500">
        {data
          ? `${data.clients} client${data.clients === 1 ? 'e' : 'i'} · ${data.professionals} consulent${data.professionals === 1 ? 'e' : 'i'}`
          : 'caricamento…'}
      </p>
    </div>
  );
}

export function AdminRevenuePage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const dateParam = searchParams.get('date');
  const date = dateParam && /^\d{4}-\d{2}-\d{2}$/.test(dateParam) ? dateParam : todayLocal();
  const setDate = (d: string) => setSearchParams(d === todayLocal() ? {} : { date: d }, { replace: true });
  const navigate = useNavigate();
  const { data, isLoading } = useDailyRevenue(date);
  const isToday = date === todayLocal();

  return (
    <div className="space-y-6">
      {/* Header + day picker */}
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-neutral-900">Revenue</h1>
          <p className="mt-1 text-sm text-neutral-500">
            Depositi e consulenze del giorno, con guadagno consulente e piattaforma.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setDate(shiftDay(date, -1))}
            aria-label="Giorno precedente"
            className="rounded-lg border border-neutral-200 bg-white px-3 py-2 text-sm text-neutral-700 hover:bg-neutral-50"
          >
            ‹
          </button>
          <input
            type="date"
            value={date}
            max={todayLocal()}
            onChange={(e) => e.target.value && setDate(e.target.value)}
            className="rounded-lg border border-neutral-300 bg-white px-3 py-2 text-sm text-neutral-900 focus:border-brand-teal focus:outline-none focus:ring-1 focus:ring-brand-teal"
          />
          <button
            type="button"
            onClick={() => setDate(shiftDay(date, 1))}
            disabled={isToday}
            aria-label="Giorno successivo"
            className="rounded-lg border border-neutral-200 bg-white px-3 py-2 text-sm text-neutral-700 hover:bg-neutral-50 disabled:opacity-40"
          >
            ›
          </button>
        </div>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-24">
          <Spinner size="lg" />
        </div>
      ) : !data ? (
        <p className="py-12 text-center text-neutral-400">Nessun dato disponibile.</p>
      ) : (
        <>
          {/* Cards */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <OnlineUsersCard />
            <StatCard
              title={isToday ? 'Depositi di oggi' : 'Depositi'}
              value={`€ ${data.deposits.totalAmount.toFixed(2)}`}
              detail={`${data.deposits.count} ricarich${data.deposits.count === 1 ? 'a' : 'e'}`}
              delta={{
                current: data.deposits.totalAmount,
                previous: data.deposits.prevTotalAmount,
                compareLabel: isToday ? 'ieri' : 'giorno prec.',
              }}
            />
            <StatCard
              title={isToday ? 'Consulenze di oggi' : 'Consulenze'}
              value={String(data.calls.connected)}
              detail={`con risposta, di cui ${data.calls.completed} conclus${data.calls.completed === 1 ? 'a' : 'e'} · ${data.calls.items.length - data.calls.connected} senza risposta`}
              delta={{
                current: data.calls.connected,
                previous: data.calls.prevConnected,
                compareLabel: isToday ? 'ieri' : 'giorno prec.',
              }}
            />
            <StatCard
              title={isToday ? 'Nuovi utenti di oggi' : 'Nuovi utenti'}
              value={String(data.newUsers.count)}
              detail={`registrat${data.newUsers.count === 1 ? 'o' : 'i'} in giornata`}
              delta={{
                current: data.newUsers.count,
                previous: data.newUsers.prevCount,
                compareLabel: isToday ? 'ieri' : 'giorno prec.',
              }}
            />
          </div>

          {/* Deposits list */}
          <div className="rounded-xl border border-neutral-200 bg-white">
            <div className="border-b border-neutral-100 px-5 py-4">
              <h2 className="text-base font-semibold text-neutral-900">Depositi</h2>
            </div>
            {data.deposits.items.length === 0 ? (
              <p className="px-5 py-8 text-center text-sm text-neutral-400">Nessun deposito in questa giornata.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-neutral-100 text-left text-xs uppercase tracking-wider text-neutral-400">
                      <th className="px-5 py-3 font-medium">Ora</th>
                      <th className="px-5 py-3 font-medium">Utente</th>
                      <th className="px-5 py-3 text-right font-medium">Importo</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.deposits.items.map((d) => (
                      <tr key={d.id} className="border-b border-neutral-50 last:border-0">
                        <td className="px-5 py-3 text-neutral-500">{formatTime(d.at)}</td>
                        <td className="px-5 py-3">
                          <span className="font-medium text-neutral-900">{d.user.displayName}</span>
                          {d.user.email && <span className="ml-2 text-xs text-neutral-400">{d.user.email}</span>}
                        </td>
                        <td className="px-5 py-3 text-right font-semibold text-emerald-600">
                          € {d.amount.toFixed(2)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Calls list */}
          <div className="rounded-xl border border-neutral-200 bg-white">
            <div className="border-b border-neutral-100 px-5 py-4">
              <h2 className="text-base font-semibold text-neutral-900">Chiamate</h2>
            </div>
            {data.calls.items.length === 0 ? (
              <p className="px-5 py-8 text-center text-sm text-neutral-400">Nessuna chiamata in questa giornata.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-neutral-100 text-left text-xs uppercase tracking-wider text-neutral-400">
                      <th className="px-5 py-3 font-medium">Ora</th>
                      <th className="px-5 py-3 font-medium">Chiamante</th>
                      <th className="px-5 py-3 font-medium">Consulente</th>
                      <th className="px-5 py-3 font-medium">Tipo</th>
                      <th className="px-5 py-3 text-center font-medium">Risposta</th>
                      <th className="px-5 py-3 font-medium">Stato</th>
                      <th className="px-5 py-3 font-medium">Tariffa</th>
                      <th className="px-5 py-3 text-right font-medium">Durata</th>
                      <th className="px-5 py-3 text-right font-medium">€ Consulente</th>
                      <th className="px-5 py-3 text-right font-medium">€ Piattaforma</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.calls.items.map((c) => (
                      <tr
                        key={c.id}
                        onClick={() => navigate(`/dashboard/admin/calls/${c.id}?date=${date}`)}
                        className="cursor-pointer border-b border-neutral-50 last:border-0 hover:bg-neutral-50"
                      >
                        <td className="px-5 py-3 text-neutral-500">{formatTime(c.at)}</td>
                        <td className="px-5 py-3 font-medium text-neutral-900">{c.caller.displayName}</td>
                        <td className="px-5 py-3 text-neutral-700">
                          {c.professional.displayName}
                          {c.isTest && (
                            <span className="ml-2 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-800">
                              PROVA
                            </span>
                          )}
                        </td>
                        <td className="px-5 py-3 text-neutral-500">{callTypeLabels[c.callType] ?? c.callType}</td>
                        <td className="px-5 py-3 text-center">
                          <AnsweredBadge answered={c.answered} />
                        </td>
                        <td className="px-5 py-3 text-neutral-500">
                          {c.status === 'completed' && !c.answered ? 'Non connessa' : statusLabels[c.status] ?? c.status}
                        </td>
                        <td className="px-5 py-3">
                          <TariffBadges segments={c.tariff.segments} />
                        </td>
                        <td className="px-5 py-3 text-right font-mono text-neutral-700">{formatDuration(c.durationSeconds)}</td>
                        <td className="px-5 py-3 text-right text-neutral-900">
                          {formatEuro(c.consultantEarning)}
                          {c.conversionEarning != null && (
                            <span className="ml-1 rounded-full bg-brand-teal-50 px-1.5 py-0.5 text-xs font-medium text-brand-teal-600">
                              +{formatEuro(c.conversionEarning)} conversione
                            </span>
                          )}
                        </td>
                        <td className="px-5 py-3 text-right font-semibold text-brand-teal">{formatEuro(c.platformEarning)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="border-t border-neutral-200 bg-neutral-50">
                      <td colSpan={8} className="px-5 py-3 text-right text-xs font-medium uppercase tracking-wider text-neutral-400">
                        Totali (include eventuali bonus conversione)
                      </td>
                      <td className="px-5 py-3 text-right font-bold text-neutral-900">
                        {formatEuro(data.calls.totalConsultant)}
                      </td>
                      <td className="px-5 py-3 text-right font-bold text-brand-teal">
                        {formatEuro(data.calls.totalPlatform)}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
